export type TransmissionType = 'automatic' | 'manual';
export type AutomaticGear = 'P' | 'R' | 'N' | 'D';

export interface PowertrainState {
  transmissionType: TransmissionType;
  autoGear: AutomaticGear;
  manualGear: number; // -1 = R, 0 = N, 1..6 = Gears
  effectiveGear: number; // actual forward gear ratio (1..6) or -1 (R) or 0 (N)
  rpm: number;
  engineRunning: boolean;
  isStalled: boolean;
  clutchPedal: number; // 0.0 = fully released/engaged, 1.0 = fully depressed/disengaged
  engineLoad: number;
  wheelTorque: number;
  speedKmh: number;
}

export class Powertrain {
  public type: TransmissionType = 'automatic';
  public autoGear: AutomaticGear = 'P';
  public manualGear: number = 1; // 1st gear default in manual
  
  public rpm: number = 850;
  public readonly idleRpm: number = 850;
  public readonly maxRpm: number = 6500;
  public readonly stallRpm: number = 550;

  public engineRunning: boolean = false;
  public isStalled: boolean = false;
  public clutchPedal: number = 0.0; // 0 = connected, 1 = pressed
  public readonly bitePointCenter: number = 0.45; // Schleifpunkt center (0.28..0.65 window)

  public isAtBitePoint(): boolean {
    return this.type === 'manual' && this.clutchPedal >= 0.28 && this.clutchPedal <= 0.65;
  }

  // Gear ratios for a 6-speed German hatchback
  public readonly gearRatios = [
    3.82,  // 1st
    2.15,  // 2nd
    1.42,  // 3rd
    1.03,  // 4th
    0.82,  // 5th
    0.68   // 6th
  ];
  public readonly reverseRatio = 3.65;
  public readonly finalDriveRatio = 3.85;

  private autoShiftTimer = 0;

  constructor(type: TransmissionType = 'automatic') {
    this.type = type;
    this.engineRunning = false;
    this.rpm = 0;
    if (type === 'manual') {
      this.clutchPedal = 1.0; // Start with clutch depressed in manual
      this.manualGear = 1;
    } else {
      this.autoGear = 'P'; // Always start in Park — car won't move until user selects D
    }
  }

  public setTransmissionType(type: TransmissionType) {
    this.type = type;
    if (type === 'manual') {
      this.manualGear = 1;
      this.clutchPedal = 1.0; // start with clutch depressed so they can select gear
    } else {
      this.autoGear = 'P'; // Start in Park when switching to automatic
      this.clutchPedal = 0.0;
    }
    this.isStalled = false;
    this.engineRunning = false;
    this.rpm = 0;
  }

  public startEngine() {
    this.isStalled = false;
    this.engineRunning = true;
    this.rpm = this.idleRpm;
  }

  public stopEngine() {
    this.engineRunning = false;
    this.rpm = 0;
  }

  public toggleEngine() {
    if (this.engineRunning && !this.isStalled) {
      this.stopEngine();
    } else {
      this.startEngine();
    }
  }

  public restartEngine() {
    this.startEngine();
  }

  public getGearDisplay(currentSpeedKmh: number = 0): string {
    const ptState = this.getState(currentSpeedKmh);
    return ptState.transmissionType === 'automatic'
      ? `${ptState.autoGear}${ptState.autoGear === 'D' ? ptState.manualGear : ''}`
      : ptState.manualGear === -1 ? 'R' : ptState.manualGear === 0 ? 'N' : `${ptState.manualGear}`;
  }

  public update(deltaTime: number, throttle: number, brake: number, currentSpeedKmh: number): {
    engineTorque: number;
    brakeForce: number;
    isReverse: boolean;
    isStalled: boolean;
    rpm: number;
  } {
    if (!this.engineRunning || this.isStalled) {
      this.rpm = Math.max(0, this.rpm - 2500 * deltaTime);
      return {
        engineTorque: 0,
        brakeForce: brake * 3500 + 400, // Resistance when stalled
        isReverse: false,
        isStalled: true,
        rpm: this.rpm,
      };
    }

    let effectiveGear = 0;
    let isReverse = false;

    if (this.type === 'automatic') {
      effectiveGear = this.updateAutomaticGear(deltaTime, currentSpeedKmh, throttle);
      isReverse = this.autoGear === 'R';
    } else {
      effectiveGear = this.manualGear;
      isReverse = this.manualGear === -1;
      this.checkManualStall(deltaTime, throttle, currentSpeedKmh);
    }

    if (this.isStalled) {
      return {
        engineTorque: 0,
        brakeForce: brake * 3500 + 600,
        isReverse: false,
        isStalled: true,
        rpm: this.rpm,
      };
    }

    // Calculate RPM based on wheel speed & gear ratio
    const wheelRadius = 0.32; // meters
    const wheelCircumference = 2 * Math.PI * wheelRadius;
    const speedMs = Math.abs(currentSpeedKmh) / 3.6;
    const wheelRps = speedMs / wheelCircumference;

    let gearRatio = 0;
    if (isReverse) {
      gearRatio = this.reverseRatio;
    } else if (effectiveGear > 0 && effectiveGear <= 6) {
      gearRatio = this.gearRatios[effectiveGear - 1];
    }

    const totalRatio = gearRatio * this.finalDriveRatio;
    const mechanicalRpm = wheelRps * 60 * totalRatio;

    // Clutch engagement factor (0.0 = completely slipping, 1.0 = fully locked)
    let clutchEngagement = 1.0;
    if (this.type === 'manual') {
      // 0 = released (engaged), 1 = pressed (disengaged)
      clutchEngagement = Math.max(0, Math.min(1, (1.0 - this.clutchPedal) * 1.5));
    } else {
      // Automatic transmission: fully engaged when in Drive (D) or Reverse (R)
      clutchEngagement = (this.autoGear === 'D' || this.autoGear === 'R') ? 1.0 : 0.0;
    }

    // Compute RPM target
    const targetRpm = effectiveGear === 0 || this.autoGear === 'N' || this.autoGear === 'P'
      ? this.idleRpm + throttle * 4800
      : Math.max(this.idleRpm, mechanicalRpm * 0.75 + (this.idleRpm + throttle * 2800) * 0.25);

    // Smooth RPM response
    this.rpm += (targetRpm - this.rpm) * Math.min(1.0, deltaTime * 12);
    this.rpm = Math.min(this.maxRpm, Math.max(this.idleRpm, this.rpm));

    // Realistic engine torque curve (Peak torque at ~2500-3800 RPM)
    const rpmNorm = (this.rpm - this.idleRpm) / (this.maxRpm - this.idleRpm);
    const torqueCurve = Math.max(0.65, Math.sin(rpmNorm * Math.PI * 0.85 + 0.15));
    const maxEngineTorque = 280; // Nm (1.5L TSI German engine)
    const availableTorque = maxEngineTorque * torqueCurve;

    let netTorque = availableTorque * throttle * totalRatio * clutchEngagement;

    // Disengage torque in Neutral or Park
    if (effectiveGear === 0 || this.autoGear === 'N' || this.autoGear === 'P') {
      netTorque = 0;
    }

    // Automatic idle creep ("Standgas-Kriechen"):
    // Smoothly assists low-speed rolling at ~4-6 km/h when in D or R without throttle.
    // CRITICAL: Creep NEVER initiates movement from standstill — it only SUSTAINS
    // already-rolling movement above 2 km/h. The driver must press gas to start moving.
    // At complete standstill, the car remains held safely by Auto-Hold until throttle is pressed.
    if (this.type === 'automatic' && throttle < 0.05 && brake < 0.04 && this.engineRunning && !this.isStalled) {
      if (this.autoGear === 'D' && currentSpeedKmh > 2.0 && currentSpeedKmh < 6.5) {
        const creepFactor = Math.max(0, 1.0 - currentSpeedKmh / 6.5);
        netTorque = 38 * totalRatio * creepFactor;
      } else if (this.autoGear === 'R' && currentSpeedKmh < -2.0 && currentSpeedKmh > -6.5) {
        const creepFactor = Math.max(0, 1.0 - Math.abs(currentSpeedKmh) / 6.5);
        netTorque = 38 * totalRatio * creepFactor;
      }
    }

    // Manual transmission clutch bite-point creep ("Kupplung-Schleifpunkt-Kriechen"):
    // Allows smooth 4–6 km/h parking & maneuvering in 1st or Reverse gear using the clutch bite point alone.
    if (
      this.type === 'manual' &&
      this.isAtBitePoint() &&
      (this.manualGear === 1 || this.manualGear === -1) &&
      brake < 0.05 &&
      this.engineRunning &&
      !this.isStalled &&
      Math.abs(currentSpeedKmh) < 7.0
    ) {
      const biteFactor = Math.max(0, 1.0 - Math.abs(this.clutchPedal - this.bitePointCenter) / 0.22);
      const speedFactor = Math.max(0, 1.0 - Math.abs(currentSpeedKmh) / 7.0);
      const creepTorque = biteFactor * 42 * totalRatio * speedFactor;
      netTorque = Math.max(netTorque, creepTorque);
    }

    // Engine braking when off throttle — only above 5 km/h
    if (throttle < 0.05 && speedMs > 1.5 && effectiveGear !== 0 && netTorque === 0) {
      netTorque = -40 * gearRatio * clutchEngagement;
    }

    const totalBrakeForce = brake * 4200 + (this.autoGear === 'P' ? 8000 : 0);

    return {
      engineTorque: netTorque,
      brakeForce: totalBrakeForce,
      isReverse,
      isStalled: false,
      rpm: this.rpm,
    };
  }

  private updateAutomaticGear(deltaTime: number, speedKmh: number, throttle: number): number {
    if (this.autoGear === 'P' || this.autoGear === 'N') return 0;
    if (this.autoGear === 'R') return -1;

    this.autoShiftTimer += deltaTime;
    if (this.autoShiftTimer < 0.4) return this.manualGear; // Keep previous gear temporarily

    // Shift thresholds in km/h
    const shiftUps = [0, 22, 42, 62, 85, 115];
    const shiftDowns = [0, 14, 30, 48, 68, 92];

    let targetGear = this.manualGear;
    // Kickdown adjustment if heavy throttle
    const throttleBonus = throttle > 0.8 ? -8 : 0;

    if (this.manualGear < 6 && speedKmh > shiftUps[this.manualGear] + throttleBonus) {
      targetGear = this.manualGear + 1;
      this.autoShiftTimer = 0;
    } else if (this.manualGear > 1 && speedKmh < shiftDowns[this.manualGear - 1] + throttleBonus) {
      targetGear = this.manualGear - 1;
      this.autoShiftTimer = 0;
    }

    this.manualGear = Math.max(1, Math.min(6, targetGear));
    return this.manualGear;
  }

  private checkManualStall(deltaTime: number, throttle: number, speedKmh: number) {
    if (this.manualGear === 0) return; // Neutral won't stall

    // If clutch is dropped past the bite point (pedal < 0.25) while car is stationary and low throttle
    const clutchDroppedPastBite = this.clutchPedal < 0.25;
    const isStationary = Math.abs(speedKmh) < 2.5;

    if (clutchDroppedPastBite && isStationary && throttle < 0.15) {
      // Rapid clutch drop stall
      this.isStalled = true;
      this.engineRunning = false;
    }
  }

  public getState(currentSpeedKmh: number): PowertrainState {
    return {
      transmissionType: this.type,
      autoGear: this.autoGear,
      manualGear: this.manualGear,
      effectiveGear: this.type === 'automatic' ? (this.autoGear === 'R' ? -1 : this.autoGear === 'D' ? this.manualGear : 0) : this.manualGear,
      rpm: this.rpm,
      engineRunning: this.engineRunning,
      isStalled: this.isStalled,
      clutchPedal: this.clutchPedal,
      engineLoad: this.rpm / this.maxRpm,
      wheelTorque: 0,
      speedKmh: currentSpeedKmh,
    };
  }

  public shiftUp(speedKmh: number) {
    if (this.type === 'automatic') {
      const cycle: AutomaticGear[] = ['P', 'R', 'N', 'D'];
      const idx = cycle.indexOf(this.autoGear);
      if (idx < cycle.length - 1) {
        this.autoGear = cycle[idx + 1];
      }
    } else {
      if (this.manualGear < 6) {
        this.manualGear++;
      }
    }
  }

  public shiftDown(speedKmh: number) {
    if (this.type === 'automatic') {
      const cycle: AutomaticGear[] = ['P', 'R', 'N', 'D'];
      const idx = cycle.indexOf(this.autoGear);
      if (idx > 0) {
        this.autoGear = cycle[idx - 1];
      }
    } else {
      if (this.manualGear > -1) {
        this.manualGear--;
      }
    }
  }
}
